import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check } from 'lucide-react';
import { Button, cn } from '@bliks-io/ui';
import { Header } from '@/layout/header/Header';

interface WizardShellProps {
  /** The spine on the left — a `WizardRail`, or nothing on an intro screen. */
  rail?: ReactNode;
  /**
   * `page`: the wizard owns the whole viewport, with the app header on top —
   * for a wizard that replaces the app until it is done. `embedded`: it fills
   * whatever its parent gives it — for a wizard inside a page.
   */
  chrome?: 'page' | 'embedded';
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
  nextLabel: string;
  /** Label of the skip button; required when `onSkip` is given */
  skipLabel?: string;
  /** A closing step renders a leading check instead of the trailing arrow */
  nextIcon?: 'arrow' | 'check';
  /** Intro screens render full-width on white. */
  isIntro?: boolean;
  isSaving?: boolean;
  /** Blocks Weiter while the step is not ready to leave. */
  nextDisabled?: boolean;
  /** Rendered in the footer left of the buttons: an autosave readout, a hint. */
  footerExtra?: ReactNode;
  children: ReactNode;
}

/**
 * The frame every wizard shares: rail on the left, the step in the middle,
 * Zurück / Überspringen / Weiter in the footer. What the steps are, and what
 * happens on Weiter, is the wizard's.
 */
export function WizardShell({
  rail,
  chrome = 'page',
  onBack,
  onNext,
  onSkip,
  nextLabel,
  skipLabel,
  nextIcon = 'arrow',
  isIntro = false,
  isSaving = false,
  nextDisabled = false,
  footerExtra,
  children
}: WizardShellProps) {
  const { t } = useTranslation();

  return (
    <div className={cn('flex flex-col', chrome === 'page' ? 'h-screen' : 'h-full min-h-0')}>
      {chrome === 'page' && <Header />}

      <div className="flex min-h-0 flex-1">
        {rail}

        <div
          className={cn('flex min-w-0 flex-1 flex-col', isIntro ? 'bg-white' : 'bg-surface-subtle')}
        >
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

          <div className="flex items-center justify-between border-t border-line bg-white px-10 py-4">
            <div>
              {onBack && (
                <Button variant="outline" size="medium" onClick={onBack} disabled={isSaving}>
                  {t('common.back')}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3.5">
              {footerExtra}
              {onSkip && (
                <Button variant="ghost" size="medium" onClick={onSkip} disabled={isSaving}>
                  {skipLabel}
                </Button>
              )}
              <Button
                variant="brand"
                size="medium"
                onClick={onNext}
                loading={isSaving}
                disabled={isSaving || nextDisabled}
              >
                {nextIcon === 'check' && <Check className="size-4!" strokeWidth={2.5} />}
                {nextLabel}
                {nextIcon === 'arrow' && <ArrowRight className="size-4!" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
